import { Box } from "@mui/material"
import {
  FocusContext,
  getCurrentFocusKey,
  navigateByDirection,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useCallback, useEffect, useRef } from "react"
import { Outlet, useLocation, useMatch } from "react-router"
import { useNavigate } from "react-router-dom"
import { useTransitions } from "src/hooks/use-transitions"

import { Pages } from "../App"
import useSdk, { ErrorLevel } from "../hooks/use-sdk"
import { useSmoothNavigate } from "../hooks/use-smooth-navigate"
import useTranslate, { Namespaces } from "../hooks/use-translate"
import { useEventBind } from "../hooks/useEventBind"
import { appStore, useAuth } from "../store/AppStore"
import { focusStore } from "../store/FocusStore"
import { getDeviceInfoStrings } from "../utilities/auth"
import { useChromecastListener } from "../utilities/chromecast"
import {
  CuratedContentSlugV2,
  exitApp,
  getPlayerCapabilities,
  removeSlashes,
} from "../utilities/common"
import {
  Buttons,
  keydown,
  locale,
  Platform,
  platform,
} from "../utilities/constants"
import { setSentryTag, setSentryUser } from "../utilities/sentry"

import { PromptModal } from "./Prompt/PromptModal"

export const Root = () => {
  const { focusKey, ref } = useFocusable()
  const backButtonOverride = appStore((state) => state.backButtonOverride)
  const setCurrentUser = appStore((state) => state.setCurrentUser)
  const accessTokenState = appStore((state) => state.accessTokenState)
  const setPrompt = appStore((state) => state.setPrompt)
  const setSlug = appStore((state) => state.setSlug)
  const setGeoInfo = appStore((state) => state.setGeoInfo)
  const t = useTranslate()
  const t_common = useTranslate(Namespaces.Common)

  const setTabBarSlug = focusStore((state) => state.setTabBarSlug)
  const location = useLocation()
  const smoothNavigate = useSmoothNavigate()
  const navigate = useNavigate()
  const matches = useMatch(CuratedContentSlugV2.Discover as string)
  const sdk = useSdk(ErrorLevel.NoError)
  // eventlisteners don't update with state updates, we need to use useRef here
  const currentMatches = useRef<string | undefined>(null)
  const { onTokenExpired } = useAuth()

  // reserved for chromecast
  useChromecastListener()

  // add eventlistener once / ref helper
  const registerOnce = useRef(false)
  const scrollGuard = useRef(false)

  const transition = useTransitions()

  useEffect(() => {
    document.getElementById("root")!.style.transition = transition
  }, [transition])

  const getGeoInfo = async () => {
    const countryCode = (await sdk.geoInfo())?.geoinfo?.countryCode
    setGeoInfo({ countryCode })
  }

  // override mouse wheel in the entire app
  useEventBind(window, "wheel", (e: WheelEvent) => {
    if (e.deltaY === 0) return
    if (scrollGuard.current) return
    scrollGuard.current = true
    navigateByDirection(e.deltaY < 0 ? "up" : "down", {
      noScrollTeaser: true,
    })
    setTimeout(() => (scrollGuard.current = false), 250)
  })

  const setupParallel = async () => {
    const deviceInfo = await getDeviceInfoStrings()
    setSentryTag("device_id", deviceInfo.device_id)
    setSentryTag("device_info", deviceInfo.device_info)
  }

  const setupAsync = async () => {
    // initialize player capabilities
    await getPlayerCapabilities()

    if (location.pathname === `/${Pages.Home}` || location.pathname === `/`) {
      const onboarding =
        !!window.localStorage.getItem("onboardingPlaceholder") ||
        process.env.REACT_APP_TEST ||
        platform === Platform.Chromecast
      if (!locale) navigate(`/${Pages.LocaleSelect}`, { replace: true })
      else if (onboarding) navigate(`/${Pages.Home}`, { replace: true })
      else navigate(`/${Pages.Onboarding}`, { replace: true })
    }

    // extra info modal for web
    if (platform === Platform.Web && !process.env.REACT_APP_TEST) {
      const dontshow = window.localStorage.getItem("dontshowinfo")
      setTimeout(() => {
        if (dontshow !== "true")
          setPrompt({
            open: true,
            onConfirm: () => setFocus("TABBAR"),
            onClose: () => {
              window.localStorage.setItem("dontshowinfo", "true")
              setFocus("TABBAR")
            },
            confirmButtonText: "OK",
            dismissButtonText: "Don't show again",
            dismissible: true,
            title: "Web Special Keys",
            message:
              "Back: F1, Play: F2, Pause: F3, Prev. Chapter: F4, Next Chapter: F5",
            icon: "ALERT",
          })
      }, 500)
    }

    // fill translated text into offline indicator inside index.html
    const offlineIndicator = document.getElementById("offline-indicator-text")
    if (offlineIndicator)
      offlineIndicator.innerText = t(
        "component__myStage_segmentControl_offline",
      )
  }

  useEffect(() => {
    getGeoInfo()
    setupParallel()
    setupAsync()
  }, [])

  useEffect(() => {
    if (accessTokenState) {
      sdk
        .currentUser()
        .then((data) => {
          setCurrentUser(data.currentUser)
          setSentryUser({
            email: data.currentUser?.email,
            id: data.currentUser?.id,
          })
        })
        .catch(() => {
          // The error code is irrelevant. Try to fetch new token if we have a refresh token anyway
          onTokenExpired()
        })
    } else {
      setCurrentUser(null)
      setSentryUser({
        email: undefined,
        id: undefined,
      })
    }
  }, [accessTokenState])

  const exitHandler = useCallback(
    (returnFocus: string) => {
      setPrompt({
        open: true,
        onClose: () => setFocus(returnFocus),
        onConfirm: exitApp,
        confirmButtonText: t_common("confirm__confirm"),
        dismissButtonText: t_common("confirm__cancel"),
        dismissible: true,
        title: t("tv__quit_title_smarttv"),
        message: t("tv__quit_subtitle_smarttv"),
        icon: "ALERT",
      })
    },
    [setFocus, setPrompt, smoothNavigate],
  )

  const bbHandler = useCallback(
    (e: KeyboardEvent) => {
      if (e.keyCode === Buttons.Back) {
        if (backButtonOverride.length > 0) return
        const locationHelper = removeSlashes(location.pathname)
        if (locationHelper === Pages.LocaleSelect) return
        if (locationHelper === Pages.Home) {
          if (
            !getCurrentFocusKey() ||
            !getCurrentFocusKey().startsWith("TABBAR")
          ) {
            setFocus("TABBAR")
            return
          } else if (
            getCurrentFocusKey() !==
            "TABBAR:" + CuratedContentSlugV2.Discover
          ) {
            setSlug(CuratedContentSlugV2.Discover)
            setTabBarSlug(CuratedContentSlugV2.Discover)
            setFocus("TABBAR:" + CuratedContentSlugV2.Discover)
            return
          } else {
            exitHandler("TABBAR")
            return
          }
        }
        if (locationHelper === Pages.Onboarding) {
          exitHandler(getCurrentFocusKey())
          return
        }
        smoothNavigate(-1)
      }
    },
    [location, backButtonOverride],
  )

  useEffect(() => {
    currentMatches.current = matches?.pathname
  }, [matches?.pathname])

  useEffect(() => {
    if (registerOnce.current) return
    registerOnce.current = true
  }, [backButtonOverride, location.pathname])
  useEventBind(window, keydown, bbHandler)

  return (
    <FocusContext.Provider value={focusKey}>
      <Box
        ref={ref}
        className={"App"}
        sx={{ display: "flex", flexDirection: "column" }}
      >
        <Outlet />
        <PromptModal />
      </Box>
    </FocusContext.Provider>
  )
}
